import { creatorbase } from '@round/api';
import { ColumnDef } from '@tanstack/react-table';
import styles from './ProjectSelectTable.module.css';
import { useMemo } from 'react';
import { Checkbox } from '@round/whitelabel-ui';
import { VirtualizedTable, VirtualizedTableProps } from '@round/whitelabel-ui';
import ProjectCell from '../../../../Project/components/cells/ProjectCell/ProjectCell';
import BrandCell from '../../../../Project/components/cells/BrandCell/BrandCell';
import Skeleton from 'react-loading-skeleton';
import PlatformIcon from '../../../../components/PlatformIcon';
import cn from 'classnames';
import UpdatedCell from '../../../../Project/components/cells/UpdatedCell';
import DurationCell from '../../../../Project/components/cells/DurationCell';
import UsersCell from '../../../../Project/components/cells/UsersCell/UsersCell';
import { ProjectSelectTableRow, Meta, getTableMeta } from './helpers';

type Props = Pick<
    VirtualizedTableProps<ProjectSelectTableRow>,
    'data' | 'rowSelection' | 'onRowSelectionChange' | 'onEndReached' | 'containerClassName'
> & {
    meta: Meta;
};

const ProjectSelectTable = ({
    data,
    rowSelection,
    onRowSelectionChange,
    onEndReached,
    containerClassName,
    meta,
}: Props) => {
    const { isLoading, isFetchingNextPage, hasError } = meta;
    const columns = useMemo<ColumnDef<ProjectSelectTableRow, any>[]>(
        () => [
            {
                id: 'select',
                meta: {
                    className: styles.checkboxColumn,
                },
                header: '',
                cell: ({ row, table }) => {
                    const { getIsRowLoading } = getTableMeta(table);

                    if (getIsRowLoading(row.original)) {
                        return <Skeleton width="1rem" />;
                    }

                    return (
                        <Checkbox
                            ariaLabel="Select row"
                            value={row.getIsSelected()}
                            onChange={() => row.toggleSelected()}
                        />
                    );
                },
            },
            {
                header: 'Artist',
                accessorKey: 'brand',
                meta: {
                    className: styles.brandColumn,
                },
                cell: BrandCell,
            },
            {
                header: 'Project',
                id: 'project',
                accessorKey: 'name',
                meta: {
                    className: styles.projectColumn,
                },
                cell: ProjectCell,
            },
            {
                header: 'Channels',
                accessorKey: 'platforms',
                cell: ({ row: { original }, getValue, table }) => {
                    const { getIsRowLoading } = getTableMeta(table);
                    if (getIsRowLoading(original)) {
                        return <Skeleton width="10rem" />;
                    }

                    return creatorbase.platforms.map((platform) => (
                        <PlatformIcon
                            className={cn(styles.platformIcon, { [styles.ghostIcon]: !getValue().includes(platform) })}
                            key={platform}
                            platform={platform}
                            colored={getValue().includes(platform)}
                        />
                    ));
                },
            },
            {
                header: 'Last updated',
                accessorKey: 'associations_updated_at',
                cell: UpdatedCell,
            },
            {
                header: 'Duration',
                id: 'duration',
                cell: DurationCell,
            },
            {
                header: 'Planners',
                accessorKey: 'users',
                cell: UsersCell,
            },
        ],
        []
    );

    const emptyRows = new Array(25).fill({}) as ProjectSelectTableRow[];
    const rows = isLoading || isFetchingNextPage ? data.concat(emptyRows) : data;

    return (
        <VirtualizedTable
            data={rows}
            columns={columns}
            meta={meta}
            className={styles.table}
            containerClassName={containerClassName}
            noDataLabel={
                <div aria-label="no projects" className={styles.noDataContainer}>
                    {hasError ? "Couldn't fetch projects" : 'No projects found'}
                </div>
            }
            enableRowSelection
            enableMultiRowSelection
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            virtualizerOptions={{
                count: rows.length,
                estimateSize: () => 50,
                getItemKey: (index) => rows[index]?.id || index,
                overscan: 5,
            }}
            onEndReached={onEndReached}
            getRowId={(row, index) => (typeof row.id === 'number' ? row.id.toString() : `skeleton-${index}`)}
        />
    );
};

export default ProjectSelectTable;
